import React, { useEffect, useMemo, useState } from 'react';
import './Cards.css';
import './MaintRecordModal.css';
import { GreenIcon, YellowIcon, RedIcon, GrayIcon } from './SignalIcons';
import {
    getMaintRecords,
    createMaintRecord,
    updateMaintRecord,
    deleteMaintRecord,
} from '../services/maintRecord';
import { changeAlertManual, getDeviceInfo } from '../services/device';
import { TimePicker, DatePicker } from 'antd';
import dayjs from 'dayjs';

export default function MaintRecordModal({ deviceId, onClose, styleColor }) {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deviceInfo, setDeviceInfo] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        CreatedAt: '',
        PreviousStatus: '', // green/yellow/red/gray
        NewStatus: '', // green/yellow/red/gray
        AbnormalReason: '',
        OnSiteMaintenance: '',
        ManagementChange: '',
        Remarks: '',
        AssignedPersonNotes: '',
    });
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showCreatePopup, setShowCreatePopup] = useState(false);
    const [showUpdatePopup, setShowUpdatePopup] = useState(false);
    const [creating, setCreating] = useState(false);
    const [updateTarget, setUpdateTarget] = useState(null);
    const [updateAxis, setUpdateAxis] = useState('both');
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [updateNote, setUpdateNote] = useState('');

    useEffect(() => {
        // Lock body scroll while the modal or its popups are open
        const isOpen = true; // The parent controls the main modal visibility
        const hasPopupOpen = showEditPopup || showCreatePopup || showUpdatePopup;

        if (isOpen || hasPopupOpen) {
            // Prevent background scroll
            document.body.style.overflow = 'hidden';
        } else {
            // Restore scroll; onClose also handles this when closing
            document.body.style.overflow = 'unset';
        }

        // Restore scroll on unmount
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showEditPopup, showCreatePopup, showUpdatePopup]);
    useEffect(() => {
        if (!deviceId) return;
        let isActive = true;
        (async () => {
            try {
                const info = await getDeviceInfo(deviceId);
                if (isActive) setDeviceInfo(info);
            } catch (e) {
                // Ignore device info errors; axis selection will be hidden
            }
        })();
        return () => {
            isActive = false;
        };
    }, [deviceId]);

    async function load() {
        if (!deviceId) return;
        setLoading(true);
        try {
            const res = await getMaintRecords(deviceId);
            // Accept raw array or { data: [] }
            const raw = Array.isArray(res)
                ? res
                : Array.isArray(res?.data)
                  ? res.data
                  : [];
            // Normalize keys so the UI can rely on PascalCase fields
            const normalized = raw.map((r) => ({
                id: r?.id ?? r?.recordId ?? r?.RecordId ?? r?._id ?? undefined,
                PreviousStatus:
                    r?.PreviousStatus ?? r?.previous_status ?? r?.previousStatus ?? '',
                NewStatus: r?.NewStatus ?? r?.new_status ?? r?.newStatus ?? '',
                AbnormalReason:
                    r?.AbnormalReason ?? r?.abnormal_reason ?? r?.abnormalReason ?? '',
                OnSiteMaintenance:
                    r?.OnSiteMaintenance ??
                    r?.on_site_maintenance ??
                    r?.onSiteMaintenance ??
                    '',
                ManagementChange:
                    r?.ManagementChange ??
                    r?.management_change ??
                    r?.managementChange ??
                    '',
                Remarks: r?.Remarks ?? r?.remarks ?? '',
                AssignedPersonNotes:
                    r?.AssignedPersonNotes ??
                    r?.assigned_person_notes ??
                    r?.assignedPersonNotes ??
                    '',
                // Track whether a status change has already been triggered
                triggered_status_change:
                    r?.triggered_status_change ??
                    r?.triggeredStatusChange ??
                    r?.triggeredStatus ??
                    false,
                created_at: r?.created_at ?? r?.createdAt ?? r?.CreatedAt,
                updated_at: r?.updated_at ?? r?.updatedAt ?? r?.UpdatedAt,
            }));
            setRecords(normalized);
        } catch (e) {
            console.error(e);
            alert('讀取歷程失敗');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [deviceId]);

    const statusLabel = {
        green: '正常',
        yellow: '超過警戒值',
        red: '超過行動值',
        gray: '自動化監測超過12小時無資料回傳或尚無監測資料',
    };

    const isTiDevice = useMemo(() => {
        const typeValue =
            deviceInfo?.type || deviceInfo?.device_type || deviceInfo?.deviceType || '';
        const nameValue =
            deviceInfo?.name || deviceInfo?.device_name || deviceInfo?.deviceName || '';
        const typeText = String(typeValue).toUpperCase();
        const nameText = String(nameValue).toUpperCase();
        return typeText.includes('TI') || nameText.includes('TI');
    }, [deviceInfo]);

    function renderStatusCell(value) {
        const v = (value || '').toLowerCase();
        const label = statusLabel[v] || value || '';
        const Icon =
            v === 'green'
                ? GreenIcon
                : v === 'yellow'
                  ? YellowIcon
                  : v === 'red'
                    ? RedIcon
                    : v === 'gray'
                      ? GrayIcon
                      : null;
        return (
            <span className="maint-status">
                {Icon && <Icon />}
                <span>{label}</span>
            </span>
        );
    }

    function fmtTime(ts) {
        if (!ts) return '';
        const d = new Date(ts);
        if (isNaN(d.getTime())) return String(ts);
        return d.toLocaleString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    }

    // Get the newest record ID by created_at
    function getLatestRecordId() {
        if (!records || records.length === 0) return null;
        let latest = null;
        let max = -Infinity;
        records.forEach((r) => {
            const t = new Date(r.created_at || r.updated_at || r.CreatedAt).getTime();
            if (!isNaN(t) && t > max) {
                max = t;
                latest = r;
            }
        });
        if (!latest) return null;
        return latest.id || latest.recordId || latest._id || latest.RecordId || null;
    }

    // Manually update the signal with an optional assigned-person note
    async function handleManualChange(rec) {
        if (!rec) return;
        setUpdateTarget(rec);
        setUpdateAxis('both');
        setUpdateNote('');
        setShowUpdatePopup(true);
    }

    async function submitManualChange() {
        if (!updateTarget || updatingStatus) return;
        setUpdatingStatus(true);
        try {
            const statusMap = {
                正常: 'green',
                超過警戒值: 'yellow',
                超過行動值: 'red',
                自動化監測超過12小時無資料回傳或尚無監測資料: 'gray',
                green: 'green',
                yellow: 'yellow',
                red: 'red',
                gray: 'gray',
            };
            const targetStatus =
                statusMap[updateTarget.NewStatus] ||
                statusMap[updateTarget.PreviousStatus] ||
                null;
            if (!targetStatus) {
                alert('找不到目標燈號，無法更新');
                return;
            }
            const axisParam = isTiDevice ? updateAxis : undefined;
            await changeAlertManual(deviceId, targetStatus, { axis: axisParam });
            const updatePayload = {
                CreatedAt:
                    updateTarget.created_at ||
                    updateTarget.updated_at ||
                    updateTarget.CreatedAt ||
                    null,
                previous_status: statusMap[updateTarget.PreviousStatus] || null,
                new_status: statusMap[updateTarget.NewStatus] || null,
                abnormal_reason: updateTarget.AbnormalReason || null,
                on_site_maintenance: updateTarget.OnSiteMaintenance || null,
                management_change: updateTarget.ManagementChange || null,
                remarks: updateTarget.Remarks || null,
                assigned_person_notes: updateNote || null,
            };
            const recordId =
                updateTarget.id ||
                updateTarget.recordId ||
                updateTarget._id ||
                updateTarget.RecordId;
            if (recordId) {
                await updateMaintRecord(deviceId, recordId, updatePayload);
            }
            alert('已發送更新燈號請求');
            window.dispatchEvent(new CustomEvent('alerts:refresh'));
            // 重新載入記錄，讓 UI 更新 triggered_status_change 等欄位
            await load();
            setShowUpdatePopup(false);
            setUpdateTarget(null);
            setUpdateNote('');
        } catch (e) {
            console.error(e);
            alert('更新燈號失敗');
        } finally {
            setUpdatingStatus(false);
        }
    }

    const closeUpdatePopup = () => {
        setShowUpdatePopup(false);
        setUpdateTarget(null);
        setUpdateAxis('both');
        setUpdateNote('');
    };

    function startEdit(rec) {
        setEditingId(rec.id || rec.recordId || rec._id || rec.RecordId); // Handle multiple backend keys
        setForm({
            CreatedAt: rec.created_at || '',
            PreviousStatus: rec.PreviousStatus || '',
            NewStatus: rec.NewStatus || '',
            AbnormalReason: rec.AbnormalReason || '',
            OnSiteMaintenance: rec.OnSiteMaintenance || '',
            ManagementChange: rec.ManagementChange || '',
            Remarks: rec.Remarks || '',
            AssignedPersonNotes: rec.AssignedPersonNotes || '',
        });
        setShowEditPopup(true);
    }

    function cancelEdit() {
        setEditingId(null);
        setShowEditPopup(false);
        setForm({
            CreatedAt: '',
            PreviousStatus: '',
            NewStatus: '',
            AbnormalReason: '',
            OnSiteMaintenance: '',
            ManagementChange: '',
            Remarks: '',
            AssignedPersonNotes: '',
        });
    }

    async function saveEdit(rec) {
        const id = editingId;
        if (!id) return;
        try {
            if (!form.PreviousStatus || !form.NewStatus) {
                alert('請選擇修改前/後燈號');
                return;
            }
            // Map status to green/yellow/red/gray before sending
            const statusMap = {
                正常: 'green',
                超過警戒值: 'yellow',
                超過行動值: 'red',
                自動化監測超過12小時無資料回傳或尚無監測資料: 'gray',
                green: 'green',
                yellow: 'yellow',
                red: 'red',
                gray: 'gray',
            };
            // Convert camelCase keys to snake_case for backend
            const payload = {
                CreatedAt: form.CreatedAt || null,
                previous_status: statusMap[form.PreviousStatus] || null,
                new_status: statusMap[form.NewStatus] || null,
                abnormal_reason: form.AbnormalReason || null,
                on_site_maintenance: form.OnSiteMaintenance || null,
                management_change: form.ManagementChange || null,
                remarks: form.Remarks || null,
                assigned_person_notes: form.AssignedPersonNotes || null,
            };
            await updateMaintRecord(deviceId, id, payload);
            await load();
            cancelEdit();
        } catch (e) {
            console.error(e);
            alert('更新失敗');
        }
    }

    async function removeRecord(rec) {
        const id = rec.id || rec.recordId || rec._id || rec.RecordId;
        if (!id) return;
        if (!confirm('確定要刪除這筆歷程嗎？')) return;
        try {
            await deleteMaintRecord(deviceId, id);
            await load();
        } catch (e) {
            console.error(e);
            alert('刪除失敗');
        }
    }

    async function createRecord() {
        if (creating) return;
        try {
            if (!form.PreviousStatus || !form.NewStatus) {
                alert('請選擇修改前/後燈號');
                return;
            }
            setCreating(true);
            // Map status to green/yellow/red/gray before sending
            const statusMap = {
                正常: 'green',
                超過警戒值: 'yellow',
                超過行動值: 'red',
                自動化監測超過12小時無資料回傳或尚無監測資料: 'gray',
                green: 'green',
                yellow: 'yellow',
                red: 'red',
                gray: 'gray',
            };
            // Convert camelCase keys to snake_case for backend
            const payload = {
                created_at: form.CreatedAt || null,
                previous_status: statusMap[form.PreviousStatus] || null,
                new_status: statusMap[form.NewStatus] || null,
                abnormal_reason: form.AbnormalReason || null,
                on_site_maintenance: form.OnSiteMaintenance || null,
                management_change: form.ManagementChange || null,
                remarks: form.Remarks || null,
            };
            await createMaintRecord(deviceId, payload);
            setShowCreatePopup(false);
            setForm({
                CreatedAt: '',
                PreviousStatus: '',
                NewStatus: '',
                AbnormalReason: '',
                OnSiteMaintenance: '',
                ManagementChange: '',
                Remarks: '',
            });
            await load();
        } catch (e) {
            console.error(e);
            alert('建立失敗');
        } finally {
            setCreating(false);
        }
    }

    const latestRecordId = getLatestRecordId();

    return (
        <div className="modal-overlay">
            <div className="modal" style={{ width: '95vw', maxWidth: 1600 }}>
                <div
                    className="modal-header"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <h3 style={{ margin: 0, color: styleColor || '#000' }}>
                        維護歷程管理
                    </h3>
                    <div>
                        <button onClick={load} style={{ marginRight: 8 }}>
                            重新整理
                        </button>
                        <button
                            onClick={() => {
                                setCreating(false);
                                setShowCreatePopup(true);
                                setForm({
                                    PreviousStatus: '',
                                    NewStatus: '',
                                    AbnormalReason: '',
                                    OnSiteMaintenance: '',
                                    ManagementChange: '',
                                    Remarks: '',
                                    // Default to current hour (minutes/seconds = 00)
                                    CreatedAt: dayjs()
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0)
                                        .toISOString(),
                                });
                            }}
                        >
                            新增
                        </button>
                    </div>
                </div>

                <div
                    className="modal-body"
                    style={{ maxHeight: '65vh', overflow: 'auto', marginTop: 12 }}
                >
                    {loading ? (
                        <div>讀取中...</div>
                    ) : records.length === 0 ? (
                        <div>目前無歷程。</div>
                    ) : (
                        <table className="maint-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 120 }}>時間</th>
                                    <th style={{ width: 150 }}>修改前燈號</th>
                                    <th style={{ width: 150 }}>修改後燈號</th>
                                    <th style={{ width: 160 }}>異常原因</th>
                                    <th style={{ width: 160 }}>現場維修紀錄</th>
                                    <th style={{ width: 160 }}>管理值異動</th>
                                    <th style={{ width: 140 }}>備註</th>
                                    <th style={{ width: 160 }}>承辦人備註</th>
                                    <th style={{ width: 140 }}>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((rec) => {
                                    const id =
                                        rec.id ||
                                        rec.recordId ||
                                        rec._id ||
                                        rec.RecordId ||
                                                    Math.random();
                                    return (
                                        <tr key={id}>
                                            <td>
                                                {fmtTime(
                                                    rec.created_at || rec.updated_at
                                                )}
                                            </td>
                                            <td>
                                                {renderStatusCell(rec.PreviousStatus)}
                                            </td>
                                            <td>{renderStatusCell(rec.NewStatus)}</td>
                                            <td>{rec.AbnormalReason || ''}</td>
                                            <td>{rec.OnSiteMaintenance || ''}</td>
                                            <td>{rec.ManagementChange || ''}</td>
                                            <td>{rec.Remarks || ''}</td>
                                            <td>{rec.AssignedPersonNotes || ''}</td>
                                            <td>
                                                <div className="maint-actions">
                                                    {/* Show update button only on the latest record that has not triggered a status change */}
                                                    {!rec.triggered_status_change &&
                                                        (rec.id ||
                                                            rec.recordId ||
                                                            rec._id ||
                                                            rec.RecordId ||
                                                            null) === latestRecordId && (
                                                            <button
                                                                className="maint-btn-update"
                                                                onClick={() =>
                                                                    handleManualChange(
                                                                        rec
                                                                    )
                                                                }
                                                                style={{ marginRight: 8 }}
                                                            >
                                                                更新燈號
                                                            </button>
                                                        )}
                                                    <button
                                                        className="maint-btn-edit"
                                                        onClick={() => startEdit(rec)}
                                                    >
                                                        編輯
                                                    </button>
                                                    <button
                                                        className="maint-btn-del"
                                                        onClick={() => removeRecord(rec)}
                                                    >
                                                        刪除
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div
                    className="modal-footer"
                    style={{ marginTop: 12, textAlign: 'right' }}
                >
                    <button onClick={onClose}>關閉</button>
                </div>
            </div>

            {/* Edit popup */}
            {showEditPopup && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div
                        className="modal"
                        style={{ maxWidth: 1100, minWidth: 640, position: 'relative' }}
                    >
                        <button
                            onClick={cancelEdit}
                            style={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                fontSize: 20,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            &times;
                        </button>
                        <h3 style={{ marginTop: 0 }}>編輯歷程</h3>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16,
                                width: '100%',
                                maxWidth: 700,
                            }}
                        >
                            {/* 時間 */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    時間
                                </span>
                                <div style={{ width: '100%', minWidth: 350 }}>
                                    <DatePicker
                                        value={
                                            form.CreatedAt ? dayjs(form.CreatedAt) : null
                                        }
                                        onChange={(dt) => {
                                            if (!dt) {
                                                setForm({ ...form, CreatedAt: '' });
                                                return;
                                            }
                                            const ts = dt
                                                .hour(dt.hour())
                                                .minute(0)
                                                .second(0)
                                                .millisecond(0)
                                                .toISOString();
                                            setForm({ ...form, CreatedAt: ts });
                                        }}
                                        showTime={{ format: 'HH:mm', minuteStep: 60 }}
                                        format="YYYY-MM-DD HH:mm"
                                        allowClear
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* 燈號單選 */}
                            <div
                                style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        textAlign: 'left',
                                    }}
                                >
                                    修改前燈號
                                </span>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                        flex: 1,
                                    }}
                                >
                                    {[
                                        {
                                            label: '正常',
                                            value: 'green',
                                            icon: <GreenIcon />,
                                        },
                                        {
                                            label: '超過警戒值',
                                            value: 'yellow',
                                            icon: <YellowIcon />,
                                        },
                                        {
                                            label: '超過行動值',
                                            value: 'red',
                                            icon: <RedIcon />,
                                        },
                                        {
                                            label: '自動化監測超過12小時無資料回傳或尚無監測資料',
                                            value: 'gray',
                                            icon: <GrayIcon />,
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontWeight: 400,
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="prevStatus"
                                                value={opt.value}
                                                checked={
                                                    form.PreviousStatus === opt.value
                                                }
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        PreviousStatus: e.target.value,
                                                    })
                                                }
                                            />
                                            {opt.label}
                                            {opt.icon}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div
                                style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        textAlign: 'left',
                                    }}
                                >
                                    修改後燈號
                                </span>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                        flex: 1,
                                    }}
                                >
                                    {[
                                        {
                                            label: '正常',
                                            value: 'green',
                                            icon: <GreenIcon />,
                                        },
                                        {
                                            label: '超過警戒值',
                                            value: 'yellow',
                                            icon: <YellowIcon />,
                                        },
                                        {
                                            label: '超過行動值',
                                            value: 'red',
                                            icon: <RedIcon />,
                                        },
                                        {
                                            label: '自動化監測超過12小時無資料回傳或尚無監測資料',
                                            value: 'gray',
                                            icon: <GrayIcon />,
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontWeight: 400,
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="newStatus"
                                                value={opt.value}
                                                checked={form.NewStatus === opt.value}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        NewStatus: e.target.value,
                                                    })
                                                }
                                            />
                                            {opt.label}
                                            {opt.icon}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {/* 其餘欄位 */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    異動原因
                                </span>
                                <textarea
                                    value={form.AbnormalReason}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            AbnormalReason: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minWidth: 350,
                                        minHeight: 48,
                                        resize: 'vertical',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    現場維修紀錄
                                </span>
                                <textarea
                                    value={form.OnSiteMaintenance}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            OnSiteMaintenance: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minWidth: 350,
                                        minHeight: 48,
                                        resize: 'vertical',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    管理值異動
                                </span>
                                <textarea
                                    value={form.ManagementChange}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            ManagementChange: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minHeight: 48,
                                        resize: 'vertical',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    備註
                                </span>
                                <textarea
                                    value={form.Remarks}
                                    onChange={(e) =>
                                        setForm({ ...form, Remarks: e.target.value })
                                    }
                                    style={{
                                        width: '100%',
                                        minHeight: 48,
                                        resize: 'vertical',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    承辦人備註
                                </span>
                                <textarea
                                    value={form.AssignedPersonNotes}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            AssignedPersonNotes: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minHeight: 48,
                                        resize: 'vertical',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                        </div>
                        <div style={{ marginTop: 12, textAlign: 'right' }}>
                            <button onClick={() => saveEdit()} style={{ marginRight: 8 }}>
                                儲存
                            </button>
                            <button onClick={cancelEdit}>取消</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 更新燈號 popup */}
            {showUpdatePopup && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div
                        className="modal"
                        style={{ maxWidth: 720, minWidth: 420, position: 'relative' }}
                    >
                        <button
                            onClick={closeUpdatePopup}
                            style={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                fontSize: 20,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            &times;
                        </button>
                        <h3 style={{ marginTop: 0 }}>更新燈號</h3>
                        <div style={{ marginBottom: 12, color: '#555' }}>
                            確定要以此歷程更新燈號嗎？可填寫承辦人備註（選填）。
                        </div>
                        {isTiDevice && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 16,
                                    marginBottom: 12,
                                }}
                            >
                                <span
                                    style={{
                                        width: 90,
                                        flexShrink: 0,
                                        textAlign: 'left',
                                    }}
                                >
                                    更新軸向
                                </span>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {[
                                        { label: 'TI-13', value: '13' },
                                        { label: 'TI-24', value: '24' },
                                        { label: 'Both', value: 'both' },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontWeight: 400,
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="alertAxis"
                                                value={opt.value}
                                                checked={updateAxis === opt.value}
                                                onChange={(e) =>
                                                    setUpdateAxis(e.target.value)
                                                }
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        <textarea
                            value={updateNote}
                            onChange={(e) => setUpdateNote(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: 90,
                                resize: 'vertical',
                                boxSizing: 'border-box',
                            }}
                            placeholder="承辦人備註（可留空）"
                        />
                        <div style={{ marginTop: 12, textAlign: 'right' }}>
                            <button
                                onClick={submitManualChange}
                                style={{ marginRight: 8 }}
                                disabled={updatingStatus}
                            >
                                {updatingStatus ? '送出中...' : '送出'}
                            </button>
                            <button
                                onClick={closeUpdatePopup}
                                disabled={updatingStatus}
                            >
                                取消
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 新增 popup */}
            {showCreatePopup && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                    <div
                        className="modal"
                        style={{ maxWidth: 1100, minWidth: 640, position: 'relative' }}
                    >
                        <button
                            onClick={() => {
                                setCreating(false);
                                setShowCreatePopup(false);
                            }}
                            style={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                fontSize: 20,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            &times;
                        </button>
                        <h3 style={{ marginTop: 0 }}>新增歷程</h3>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16,
                                width: '100%',
                                maxWidth: 700,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    時間
                                </span>
                                <div style={{ width: '100%', minWidth: 350 }}>
                                    <DatePicker
                                        value={
                                            form.CreatedAt ? dayjs(form.CreatedAt) : null
                                        }
                                        onChange={(dt) => {
                                            if (!dt) {
                                                setForm({ ...form, CreatedAt: '' });
                                                return;
                                            }
                                            // 將選取的日期 + 小時 (分鐘固定為 00) 存成 ISO
                                            const ts = dt
                                                .hour(dt.hour())
                                                .minute(0)
                                                .second(0)
                                                .millisecond(0)
                                                .toISOString();
                                            setForm({ ...form, CreatedAt: ts });
                                        }}
                                        showTime={{ format: 'HH:mm', minuteStep: 60 }}
                                        format="YYYY-MM-DD HH:mm"
                                        allowClear
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                            {/* 燈號單選 */}
                            <div
                                style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        textAlign: 'left',
                                    }}
                                >
                                    修改前燈號
                                </span>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                        flex: 1,
                                    }}
                                >
                                    {[
                                        {
                                            label: '正常',
                                            value: 'green',
                                            icon: <GreenIcon />,
                                        },
                                        {
                                            label: '超過警戒值',
                                            value: 'yellow',
                                            icon: <YellowIcon />,
                                        },
                                        {
                                            label: '超過行動值',
                                            value: 'red',
                                            icon: <RedIcon />,
                                        },
                                        {
                                            label: '自動化監測超過12小時無資料回傳或尚無監測資料',
                                            value: 'gray',
                                            icon: <GrayIcon />,
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontWeight: 400,
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="prevStatus_create"
                                                value={opt.value}
                                                checked={
                                                    form.PreviousStatus === opt.value
                                                }
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        PreviousStatus: e.target.value,
                                                    })
                                                }
                                            />
                                            {opt.label}
                                            {opt.icon}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div
                                style={{ display: 'flex', alignItems: 'center', gap: 16 }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        textAlign: 'left',
                                    }}
                                >
                                    修改後燈號
                                </span>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                        flex: 1,
                                    }}
                                >
                                    {[
                                        {
                                            label: '正常',
                                            value: 'green',
                                            icon: <GreenIcon />,
                                        },
                                        {
                                            label: '超過警戒值',
                                            value: 'yellow',
                                            icon: <YellowIcon />,
                                        },
                                        {
                                            label: '超過行動值',
                                            value: 'red',
                                            icon: <RedIcon />,
                                        },
                                        {
                                            label: '自動化監測超過12小時無資料回傳或尚無監測資料',
                                            value: 'gray',
                                            icon: <GrayIcon />,
                                        },
                                    ].map((opt) => (
                                        <label
                                            key={opt.value}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontWeight: 400,
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="newStatus_create"
                                                value={opt.value}
                                                checked={form.NewStatus === opt.value}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        NewStatus: e.target.value,
                                                    })
                                                }
                                            />
                                            {opt.label}
                                            {opt.icon}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {/* 其餘欄位 */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    異動原因
                                </span>
                                <textarea
                                    value={form.AbnormalReason}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            AbnormalReason: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minWidth: 350,
                                        minHeight: 48,
                                        resize: 'vertical',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    現場維修紀錄
                                </span>
                                <textarea
                                    value={form.OnSiteMaintenance}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            OnSiteMaintenance: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minWidth: 350,
                                        minHeight: 48,
                                        resize: 'vertical',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    管理值異動
                                </span>
                                <textarea
                                    value={form.ManagementChange}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            ManagementChange: e.target.value,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        minHeight: 48,
                                        resize: 'vertical',
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                }}
                            >
                                <span
                                    style={{
                                        width: 140,
                                        flexShrink: 0,
                                        marginTop: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    備註
                                </span>
                                <textarea
                                    value={form.Remarks}
                                    onChange={(e) =>
                                        setForm({ ...form, Remarks: e.target.value })
                                    }
                                    style={{
                                        width: '100%',
                                        minHeight: 48,
                                        resize: 'vertical',
                                    }}
                                />
                            </div>
                        </div>
                        <div style={{ marginTop: 12, textAlign: 'right' }}>
                            <button
                                onClick={createRecord}
                                style={{ marginRight: 8 }}
                                disabled={creating}
                            >
                                {creating ? '新增中...' : '新增'}
                            </button>
                            <button
                                onClick={() => {
                                    setCreating(false);
                                    setShowCreatePopup(false);
                                }}
                            >
                                取消
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
